import React, { useState } from "react";
import { Box, Typography, useTheme, useMediaQuery, Button } from "@mui/material";
import InputBox from "../../Components/InputBox";
import { Link, useNavigate } from "react-router-dom";
import { UserAuthApi } from "../../api/requests/userAuth/userAuthApi";
import { handleAlert } from "../../utils/handleAlert";
import { Colors } from "../../Theme/Theme";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GroupsIcon from "@mui/icons-material/Groups";
import FiLogo from "../../Components/FiLogo";

const SignUp = () => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [usernameError, setUsernameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async () => {
    let hasError = false;
    if (!username) { setUsernameError(true); hasError = true; } else setUsernameError(false);
    if (!email) { setEmailError(true); hasError = true; } else setEmailError(false);
    if (!password) { setPasswordError(true); hasError = true; } else setPasswordError(false);
    if (!hasError) {
      try {
        setLoading(true);
        const data = await UserAuthApi.signup(username, email, password);
        if (data?.token) {
          localStorage.setItem("Token", data.token);
          localStorage.setItem("Role", data?.role);
          localStorage.setItem("user_details", JSON.stringify(data?.user));
          handleAlert(data?.msg, "success");
          navigate("/dashboard");
        } else {
          setLoading(false);
          handleAlert(data?.msg || "Something went wrong", "error");
        }
      } catch (error) { handleAlert(error.message, "error"); setLoading(false); }
    } else handleAlert("Please fill all fields", "warning");
    setLoading(false);
  };

  const handleKeyDown = (e) => { if (e.key === "Enter") handleSubmit(); };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {!isMd && (
        <Box sx={{
          flex: 1, backgroundColor: Colors.bgColor,
          display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
          px: 6, position: "relative", overflow: "hidden",
          borderRight: `1px solid ${Colors.dividerColor}`,
        }}>
          <Box sx={{ position: "relative", zIndex: 2, textAlign: "center", maxWidth: 360 }}>
            <Box sx={{ mb: 5 }}>
              <FiLogo size="lg" />
            </Box>
            <Typography variant="h4" sx={{ color: Colors.primaryTextColor, fontWeight: 700, mb: 2, lineHeight: 1.2, fontSize: "28px" }}>
              Start Scaling Your Payments
            </Typography>
            <Typography sx={{ color: Colors.secondaryColor, mb: 5, lineHeight: 1.7, fontSize: "15px" }}>
              Join thousands of businesses using FI for seamless payment processing and maximum growth.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, alignItems: "flex-start", mx: "auto", width: "fit-content" }}>
              {[
                { icon: <RocketLaunchIcon sx={{ fontSize: 18 }} />, text: "Go live within 24 hours" },
                { icon: <TrendingUpIcon sx={{ fontSize: 18 }} />, text: "Best-in-class rates" },
                { icon: <GroupsIcon sx={{ fontSize: 18 }} />, text: "50+ fintech partners" },
              ].map((item, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: "8px",
                    backgroundColor: Colors.blueSoft, border: `1px solid ${Colors.dividerColor}`,
                    display: "flex", alignItems: "center", justifyContent: "center", color: Colors.primary,
                  }}>{item.icon}</Box>
                  <Typography sx={{ color: Colors.secondaryColor, fontSize: "13px", fontWeight: 500 }}>{item.text}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: Colors.white, px: { xs: 3, md: 6 }, py: 4, overflowY: "auto" }}>
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          {isMd && <Box sx={{ mb: 4 }}><FiLogo size="sm" /></Box>}
          <Typography sx={{ fontWeight: 700, color: Colors.primaryTextColor, mb: 0.5, fontSize: "24px" }}>Create your account</Typography>
          <Typography sx={{ color: Colors.secondaryColor, mb: 4, fontSize: "14px" }}>
            Already have an account?{" "}
            <Link to="/signin" style={{ color: Colors.primary, fontWeight: 600, textDecoration: "none" }}>Sign in</Link>
          </Typography>

          <InputBox label="Full Name" placeholder="Enter your full name" type="text" value={username} required onChange={(e) => { setUsername(e.target.value); if (e.target.value) setUsernameError(false); }} error={usernameError} helperText={usernameError ? "Name is required" : ""} />
          <InputBox label="Mobile Number" placeholder="Enter your mobile number" type="text" value={email} required onChange={(e) => { setEmail(e.target.value); if (e.target.value) setEmailError(false); }} error={emailError} helperText={emailError ? "Mobile number is required" : ""} />
          <InputBox label="Password" placeholder="Create a strong password" type="password" value={password} required onChange={(e) => { setPassword(e.target.value); if (e.target.value) setPasswordError(false); }} showPasswordToggle error={passwordError} helperText={passwordError ? "Password is required" : ""} onKeyDown={handleKeyDown} />

          <Button
            fullWidth variant="contained" disabled={loading} onClick={handleSubmit}
            sx={{
              backgroundColor: Colors.primary, color: Colors.white,
              fontWeight: 600, py: 1.5, borderRadius: "10px", fontSize: "14px", mt: 1,
              boxShadow: "0 1px 3px rgba(37,99,235,0.2)",
              "&:hover": { backgroundColor: Colors.accentHover },
            }}
          >
            {loading ? "Creating account..." : "Create Account"}
          </Button>
          <Typography sx={{ color: Colors.light_gray, textAlign: "center", mt: 2, fontSize: "11px" }}>
            By creating an account, you agree to our Terms of Service and Privacy Policy.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default SignUp;
